import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import { addItemToCart } from '../../services/cart';
import emptyIllustration from '../../assets/Objects.png';
import { useLanguage } from '../../context/LanguageContext';

const getCustomerId = () => {
  if (typeof window === 'undefined') return null;
  try {
    const meta = JSON.parse(window.localStorage.getItem('myUser'));
    return meta?.customer_id || null;
  } catch {
    return null;
  }
};

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const resolveImageSource = (input) => {
  if (!input) return '';
  if (typeof input === 'string') return input;
  if (Array.isArray(input)) {
    for (const candidate of input) {
      const resolved = resolveImageSource(candidate);
      if (resolved) return resolved;
    }
    return '';
  }
  if (typeof input === 'object') {
    return (
      input.imageUrl ||
      input.url ||
      input.src ||
      input.path ||
      ''
    );
  }
  return '';
};

const normalizeFavorite = (entry) => {
  const product = entry?.product || entry || {};
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const firstVariant = variants[0] || {};
  const images = Array.isArray(product.images)
    ? product.images.map(resolveImageSource).filter(Boolean)
    : [];
  const color =
    firstVariant.colorName ||
    firstVariant.color?.name ||
    product.color ||
    '';

  return {
    id:
      entry?.favoritesId ||
      entry?.id ||
      product.productId ||
      product.id ||
      Math.random().toString(36).slice(2),
    favoriteId: entry?.favoritesId || entry?.id || null,
    productId: product.productId || product.id,
    name: product.name || 'Product',
    brand: product.brand?.name || product.brand || 'Wassini',
    image:
      resolveImageSource(entry?.image) ||
      resolveImageSource(product.image) ||
      images[0] ||
      resolveImageSource(firstVariant.image) ||
      '',
    price: product.price || product.sellingPrice || 0,
    color,
    shipping: product.shipping || 0,
    hasVariants: variants.length > 0,
  };
};

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingId, setAddingId] = useState(null);
  const customerId = useMemo(getCustomerId, []);
  const { t } = useLanguage();

  const goToProduct = (productId) => {
    if (!productId || typeof window === 'undefined') return;
    window.location.assign(
      `/product/${encodeURIComponent(productId)}`
    );
  };

  useEffect(() => {
    if (!customerId) {
      setFavorites([]);
      setLoading(false);
      setError(t('favorites.error_login'));
      return;
    }

    let cancelled = false;
    const loadFavorites = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await Api.fetchFavorites(customerId);
        const payload = response?.data?.data || response?.data || [];
        if (!cancelled) {
          setFavorites(
            Array.isArray(payload) ? payload.map(normalizeFavorite) : []
          );
        }
      } catch (err) {
        if (!cancelled) {
          setFavorites([]);
          setError(
            err?.message || t('favorites.error_generic')
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFavorites();

    return () => {
      cancelled = true;
    };
  }, [customerId, t]);

  const handleAddToCart = async (item) => {
    if (!item?.productId) return;

    if (item.hasVariants) {
      goToProduct(item.productId);
      return;
    }

    setAddingId(item.id);
    try {
      addItemToCart({
        productId: item.productId,
        name: item.name,
        image: item.image,
        priceUnit: item.price,
        quantity: 1,
        shipping: item.shipping || 0,
      });
    } finally {
      setAddingId(null);
    }
  };

  const handleRemoveFavorite = async (item) => {
 
    if (!item?.id) return;
    try {
      await Api.deleteFavorite(item.id);
      setFavorites((prev) => prev.filter((fav) => fav.id !== item.id));
    } catch {
      // ignore
    }
  };

  const emptyStateText = error || t('favorites.empty_cta');

  return (
    <div className="favorites-page">
      <header className="favorites-page__header">
        <div>
          <h2>{t('favorites.title')}</h2>
    
        </div>
      </header>

      {loading ? (
        <div className="favorites-page__state">
          <p>{t('favorites.loading')}</p>
        </div>
      ) : !favorites.length ? (
        <div className="wishlist-empty">
          <img src={emptyIllustration} alt="Empty wishlist" />
          <p>{t('favorites.empty_title')}</p>
          <p>{emptyStateText}</p>
          <button
            type="button"
            className="button"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.assign('/categories');
              }
            }}
          >
            {t('favorites.empty_button')}
          </button>
        </div>
      ) : (
        <div className="wishlist-table">
          <div className="wishlist-table__head">
            <span />
            <span>{t('favorites.table.product')}</span>
            <span>{t('favorites.table.price')}</span>
            <span>{t('favorites.table.action')}</span>
          </div>
          {favorites.map((item) => {
            const handleNavigateToProduct = () => {
              goToProduct(item.productId);
            };

            const handleProductKeyDown = (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleNavigateToProduct();
              }
            };

            return (
              <div className="wishlist-row" key={item.id}>
                <button
                  type="button"
                  className="wishlist-row__remove"
                  aria-label={t('favorites.remove')}
                  onClick={() => handleRemoveFavorite(item)}
                >
                  ×
                </button>
                <div
                  className="wishlist-row__product"
                  role="button"
                  tabIndex={0}
                  onClick={handleNavigateToProduct}
                  onKeyDown={handleProductKeyDown}
                >
                  <div className="wishlist-row__media">
                    {item.image ? (
                      <img src={item.image} alt={item.name} />
                    ) : (
                      <span>{t('favorites.no_image')}</span>
                    )}
                  </div>
                  <div>
                    <p className="wishlist-row__name">{item.name}</p>
                    {item.color && (
                      <p className="wishlist-row__color">
                        {t('favorites.color_prefix')} {item.color}
                      </p>
                    )}
                  </div>
                </div>
                <div className="wishlist-row__price">
                  <span className="wishlist-row__label">
                    {t('favorites.table.price')}
                  </span>
                  {formatCurrency(item.price)}
                </div>
                <div className="wishlist-row__action">
                  <span className="wishlist-row__label">
                    {t('favorites.table.action')}
                  </span>
                  <button
                    type="button"
                    className="button"
                    onClick={() => handleAddToCart(item)}
                    disabled={addingId === item.id}
                  >
                    {addingId === item.id
                      ? t('favorites.adding')
                      : t('favorites.add_to_cart')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;
